/**
 * General Ledger Report (BE-33)
 *
 * One GL account's movements: opening balance, every posted line with its running balance, closing balance. The account
 * and dates come from the URL (?account=&from=&to=), so the Trial Balance and Balance Sheet can open it for an account.
 * An entry number opens the journal entry.
 * Route: /reports/general-ledger
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, TextField, Alert, Skeleton, Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Autocomplete, Link, useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { fontSize as fs, fontWeight as fw } from '../../utils/accounting/styles';
import { CurrencyText, ReportExportMenu } from '../../components/accounting';
import { formatDate } from '../../utils/accounting/formatters';
import { toYmd } from '../../utils/accounting/periodUtils';
import { generalLedgerService } from '../../services/accounting/generalLedger.service';
import { journalEntryService } from '../../services/accounting/journalEntry.service';
import { serverMessage } from '../../utils/apiConvert';

const Amount = ({ value, bold }) => (value == null || Number(value) === 0
  ? <Typography component="span" sx={{ fontSize: fs.sm, color: 'text.disabled' }}>—</Typography>
  : <CurrencyText amount={value} size="sm" bold={bold} withPrefix={false} />);

// A one-sided balance as "1,234.00 Dr" / "Cr".
const Balance = ({ value, bold }) => {
  const dr = value?.debit;
  const cr = value?.credit;
  if (!dr && !cr) return <Amount value={0} />;
  return (
    <Box component="span" sx={{ display: 'inline-flex', gap: 0.5, alignItems: 'baseline' }}>
      <CurrencyText amount={dr || cr} size="sm" bold={bold} withPrefix={false} />
      <Typography component="span" sx={{ fontSize: fs.xs, color: 'text.secondary' }}>{dr ? 'Dr' : 'Cr'}</Typography>
    </Box>
  );
};

const NUM = { align: 'right', sx: { whiteSpace: 'nowrap' } };

const GeneralLedger = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const today = toYmd(new Date());
  const account = params.get('account') || '';
  const from = params.get('from') || `${today.slice(0, 7)}-01`;
  const to = params.get('to') || today;
  const [accounts, setAccounts] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const setParam = (patch) => setParams({ account, from, to, ...patch }, { replace: true });

  useEffect(() => {
    journalEntryService.getGlAccounts()
      .then((list) => setAccounts(list || []))
      .catch((err) => setError(serverMessage(err, 'Failed to load the accounts')));
  }, []);

  const load = useCallback(async () => {
    if (!account || !from || !to) { setData(null); return; }
    setLoading(true);
    setError(null);
    try {
      setData(await generalLedgerService.get({ account, from, to }));
    } catch (err) {
      setError(serverMessage(err, 'Failed to load the general ledger'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [account, from, to]);

  useEffect(() => { load(); }, [load]);

  // an inactive account is not in the picker's list: show it from the report itself
  const selected = accounts.find((a) => a.accountNumber === account)
    || (account && data?.accountNumber === account ? { accountNumber: account, name: data.accountName } : null);
  const head = { fontSize: fs.xs, fontWeight: fw.semibold, color: 'text.secondary', py: 0.75 };
  const cell = { fontSize: fs.sm, py: 0.5 };
  const band = { bgcolor: alpha(theme.palette.primary.main, 0.08) };

  return (
    <Box sx={{ p: 2, width: 0, minWidth: '100%', boxSizing: 'border-box' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography sx={{ fontSize: fs.md, fontWeight: fw.semibold }}>General Ledger</Typography>
          {data && (
            <Typography sx={{ fontSize: fs.xs, color: 'text.secondary' }}>
              {data.accountNumber} {data.accountName} · {formatDate(data.from)} to {formatDate(data.to)} · posted entries
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Autocomplete
            size="small" sx={{ width: 300 }} options={accounts} value={selected}
            getOptionLabel={(a) => `${a.accountNumber} ${a.name}`}
            isOptionEqualToValue={(a, b) => a.accountNumber === b.accountNumber}
            onChange={(e, a) => setParam({ account: a?.accountNumber || '' })}
            renderInput={(p) => <TextField {...p} label="Account" />}
          />
          <TextField label="From" type="date" size="small" value={from} onChange={(e) => setParam({ from: e.target.value })}
            InputLabelProps={{ shrink: true }} sx={{ width: 160 }} />
          <TextField label="To" type="date" size="small" value={to} onChange={(e) => setParam({ to: e.target.value })}
            InputLabelProps={{ shrink: true }} sx={{ width: 160 }} />
          {account && <ReportExportMenu reportType="general-ledger" params={{ account, from, to }} onError={setError} />}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}

      {!account ? (
        <Alert severity="info">Choose an account to see its movements.</Alert>
      ) : loading ? (
        <Box>{[1, 2, 3, 4, 5, 6].map((i) => <Skeleton key={i} height={28} sx={{ mt: 1 }} />)}</Box>
      ) : data && (
        <TableContainer sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={head}>Date</TableCell>
                <TableCell sx={head}>Entry</TableCell>
                <TableCell sx={head}>Description</TableCell>
                <TableCell sx={head}>Source</TableCell>
                <TableCell sx={head} align="right">Debit</TableCell>
                <TableCell sx={head} align="right">Credit</TableCell>
                <TableCell sx={head} align="right">Balance</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow sx={band}>
                <TableCell sx={cell}>{formatDate(data.from)}</TableCell>
                <TableCell sx={{ ...cell, fontWeight: fw.semibold }} colSpan={5}>Opening balance</TableCell>
                <TableCell {...NUM}><Balance value={data.opening} bold /></TableCell>
              </TableRow>
              {data.lines.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} sx={{ ...cell, color: 'text.secondary', textAlign: 'center', py: 2 }}>
                    Nothing posted to this account in the range.
                  </TableCell>
                </TableRow>
              )}
              {data.lines.map((l, i) => (
                <TableRow key={`${l.entryId}-${i}`} hover>
                  <TableCell sx={{ ...cell, whiteSpace: 'nowrap' }}>{formatDate(l.entryDate)}</TableCell>
                  <TableCell sx={{ ...cell, whiteSpace: 'nowrap' }}>
                    <Link component="button" underline="hover" sx={{ fontSize: fs.sm }}
                      onClick={() => navigate(`/accounting/journal-entries/${l.entryId}`)}>{l.entryNumber}</Link>
                  </TableCell>
                  <TableCell sx={{ ...cell, maxWidth: 360 }}>{l.description || '—'}</TableCell>
                  <TableCell sx={{ ...cell, color: 'text.secondary', whiteSpace: 'nowrap' }}>{(l.sourceType || '').replace(/_/g, ' ')}</TableCell>
                  <TableCell {...NUM}><Amount value={l.debit} /></TableCell>
                  <TableCell {...NUM}><Amount value={l.credit} /></TableCell>
                  <TableCell {...NUM}><Balance value={l.balance} /></TableCell>
                </TableRow>
              ))}
              <TableRow sx={band}>
                <TableCell sx={cell}>{formatDate(data.to)}</TableCell>
                <TableCell sx={{ ...cell, fontWeight: fw.bold }} colSpan={3}>Closing balance</TableCell>
                <TableCell {...NUM}><Amount value={data.periodDebit} bold /></TableCell>
                <TableCell {...NUM}><Amount value={data.periodCredit} bold /></TableCell>
                <TableCell {...NUM}><Balance value={data.closing} bold /></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default GeneralLedger;
